import { useState, useEffect } from 'react';
import { authApi } from '../services/api/auth.api';
import { useAuth } from './useAuth';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from '../shared/hooks/useToast';
import { destinoPostLogin } from '../config/producto';

interface UseLoginFlowReturn {
    step: 'email' | 'otp' | 'success';
    email: string;
    otp: string[];
    timer: number;
    devOtp: string | null;

    isSubmitting: boolean;
    isResending: boolean;
    error: string | null;
    handleEmailChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    handleEmailSubmit: (e: React.FormEvent) => Promise<void>;
    handleOtpChange: (otp: string[]) => void;
    handleOtpSubmit: () => Promise<void>; // Manual submit button
    handleOtpComplete: (code: string) => Promise<void>; // Auto submit
    handleResend: () => Promise<void>;
    handleBack: () => void;
}

export const useLoginFlow = (): UseLoginFlowReturn => {
    const [step, setStep] = useState<'email' | 'otp' | 'success'>('email');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState<string[]>(new Array(6).fill(''));
    const [timer, setTimer] = useState(600); // 10 mins default
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isResending, setIsResending] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [devOtp, setDevOtp] = useState<string | null>(null);

    const { login } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        let interval: ReturnType<typeof setInterval>;
        if (step === 'otp' && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [step, timer]);

    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value);
        setError(null);
    };

    const handleEmailSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!email || !/\S+@\S+\.\S+/.test(email)) {
            // El mismo texto que devuelve el backend para un mail que no pasa (request-otp, 400).
            setError('Ingresá un mail válido.');
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await authApi.requestOTP(email);
            if (response.success) {
                setTimer(response.data.expiresIn || 600);
                // El backend YA NO devuelve el código en la respuesta: viajaba en el body sin
                // guarda de entorno y permitía tomar la cuenta de cualquier email en 2 requests.
                // En desarrollo sale por la consola del backend. Esto queda en null y la caja
                // del código no se muestra — que es lo correcto.
                setDevOtp((response.data as { otp?: string }).otp || null);
                setStep('otp');
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error al conectar con el servidor');
        } finally {
            setIsSubmitting(false);
        }
    };

    const verifyCode = async (codeStr: string) => {
        setError(null);
        setIsSubmitting(true);
        try {
            const response = await authApi.verifyOTP(email, codeStr);
            if (response.success) {
                setStep('success');
                // Wait a bit for success animation then login
                setTimeout(() => {
                    try {
                        login({ accessToken: response.data.accessToken }, response.data.user);
                        // Vuelve al deep-link que ProtectedRoute guardó en state.from, o a HOME.
                        // replace: con «atrás» no se vuelve al formulario de login ya usado.
                        navigate(destinoPostLogin(location.state), { replace: true });
                    } catch (e) {
                        console.error('Navigation error:', e);
                    }
                }, 1500);
            }
        } catch (err: any) {
            setError(err.response?.data?.message || 'Código inválido');
            setOtp(new Array(6).fill('')); // Clear OTP on error
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOtpChange = (newOtp: string[]) => {
        setOtp(newOtp);
        setError(null);
    };

    const handleOtpComplete = async (code: string) => {
        await verifyCode(code);
    };

    const handleOtpSubmit = async () => {
        const code = otp.join('');
        if (code.length === 6) {
            await verifyCode(code);
        }
    };

    const handleResend = async () => {
        setIsResending(true);
        try {
            const response = await authApi.requestOTP(email);
            setTimer(response.data?.expiresIn || 600);
            setOtp(new Array(6).fill(''));
            setError(null);
            // DEV: capture refreshed otp
            setDevOtp(response.data?.otp || null);
            // El backend contesta lo mismo esté o no el mail en la lista: no se afirma que se mandó.
            toast.success(`Si ${email} tiene acceso, te llega un código nuevo.`);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Error reenviando código');
        } finally {
            setIsResending(false);
        }
    };

    const handleBack = () => {
        setStep('email');
        setOtp(new Array(6).fill(''));
        setError(null);
    };

    return {
        step,
        email,
        otp,
        timer,
        devOtp,
        isSubmitting,
        isResending,
        error,
        handleEmailChange,
        handleEmailSubmit,
        handleOtpChange,
        handleOtpSubmit,
        handleOtpComplete,
        handleResend,
        handleBack
    };
};
